import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { isEqual } from "lodash";
import { FilterPopup } from "../../../../../../Components/FilterPopup/filterPopup.index";
import { UserContext, UserPayload } from "../../../../../../Context/userContext";
import { AgentsFilters } from "../../../../agents.model";
import { useAgentsStore } from "../../../../store/agents.store";
import { selectFilters } from "../../../../store/agentsSelectors";
import { AgentsFilterPopupProps } from "../../agentsHeader.models";
import { buildStringListOptions, buildUserTypeOptions } from "../../agentsHeader.utils";
import { getAgentsFilterOptions } from "./agentsFilterPopup.utils";

export const AgentsFilterPopup = ({ isOpen, onClose }: AgentsFilterPopupProps) => {
    const { setShowLoader } = useContext(UserContext) as UserPayload;
    const filters = useAgentsStore(selectFilters);
    const fetchFilterLists = useAgentsStore((state) => state.fetchFilterLists);
    const fetchAllAgents = useAgentsStore((state) => state.fetchAllAgents);
    const setFilters = useAgentsStore((state) => state.setFilters);
    const agenciesList = useAgentsStore((state) => state.agenciesList);
    const agentsList = useAgentsStore((state) => state.agentsList);
    const userTypesList = useAgentsStore((state) => state.userTypesList);
    const filterListsLoading = useAgentsStore((state) => state.filterListsLoading);

    const [draftFilters, setDraftFilters] = useState<AgentsFilters>(filters);

    useEffect(() => {
        if (isOpen) {
            setDraftFilters(filters);
            fetchFilterLists();
        }
    }, [isOpen, filters, fetchFilterLists]);

    const updateDraft = useCallback(
        <K extends keyof AgentsFilters>(key: K, value: AgentsFilters[K]) =>
            setDraftFilters((prev) => ({ ...prev, [key]: value })),
        []
    );

    const userTypeOptions = useMemo(
        () => buildUserTypeOptions(userTypesList),
        [userTypesList]
    );

    const agencyOptions = useMemo(
        () => buildStringListOptions(agenciesList),
        [agenciesList]
    );

    const agentOptions = useMemo(
        () => buildStringListOptions(agentsList),
        [agentsList]
    );

    const filterOptions = useMemo(
        () =>
            getAgentsFilterOptions(
                draftFilters,
                updateDraft,
                userTypeOptions,
                agencyOptions,
                agentOptions
            ),
        [draftFilters, updateDraft, userTypeOptions, agencyOptions, agentOptions]
    );

    const handleApplyClick = useCallback(async () => {
        if (isEqual(draftFilters, filters)) {
            onClose();
            return;
        }

        setFilters(draftFilters);
        await fetchAllAgents({ setShowLoader, force: true });
        onClose();
    }, [
        draftFilters,
        filters,
        onClose,
        setFilters,
        fetchAllAgents,
        setShowLoader,
    ]);

    return (
        <FilterPopup
            isOpen={isOpen}
            onClose={onClose}
            filterOptions={filterOptions}
            onApply={handleApplyClick}
            isLoading={filterListsLoading}
        />
    );
};
